import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import {
	useLocation,
	useNavigate,
	useParams,
	useSearchParams,
} from "react-router-dom";
import { goalsQueryOptions } from "web_myplann/Api/Goals/useGoalsQuery";
import {
	ACTIVE_GOAL_STATES,
	GOAL_NAME_MAX_LENGTH,
} from "web_myplann/Goals/constants";
import { useGoalForm } from "web_myplann/Goals/Forms/useGoalForm";
import { deriveGoalAmounts } from "web_myplann/Goals/Helpers/goalAmounts";
import { isGoalEditable } from "web_myplann/Goals/Helpers/isGoalEditable";
import { useGoalCategoryFieldMemory } from "web_myplann/Goals/Hooks/useGoalCategoryFieldMemory";
import { useGoalDetail } from "web_myplann/Goals/Hooks/useGoalDetail";
import { useSuggestedGoalName } from "web_myplann/Goals/Hooks/useSuggestedGoalName";
import { mapGoalDtoToForm } from "web_myplann/Goals/Mappers/mapGoalDtoToForm";
import { mapParamsToGoalForm } from "web_myplann/Goals/Mappers/mapParamsToGoalForm";
import { mapParamsToInspirationType } from "web_myplann/Goals/Mappers/mapParamsToInspirationType";
import type { GoalFormPageState } from "web_myplann/Goals/Types/GoalFormPageState";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { getRoute } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { getTypedParam } from "web_myplann/Shared/Utils/getTypedParam";

export const useGoalFormPage = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const routeParams = useParams<{ id: string }>();
	const { state } = useLocation() as { state: GoalFormPageState | null };
	const returnToGoalsList = state?.returnToGoalsList ?? false;
	const [isOpeningCreatedGoal, setIsOpeningCreatedGoal] = useState(false);

	const goalId = getTypedParam(routeParams, "id", Number) ?? null;
	const isEdit = goalId !== null;

	const goToList = () => navigate(getRoute(Routing.Goals.GoalsList));

	const { goal, dto: goalDto } = useGoalDetail(goalId);

	useEffect(() => {
		if (!goal || goalId === null) {
			return;
		}
		if (!isGoalEditable(goal)) {
			navigate(Routing.Goals.GoalDetail.path(String(goalId)), {
				replace: true,
			});
		}
	}, [goal, goalId, navigate]);

	const initialValues = useMemo(
		() =>
			isEdit
				? goalDto
					? mapGoalDtoToForm(goalDto)
					: undefined
				: mapParamsToGoalForm(searchParams),
		[isEdit, goalDto, searchParams],
	);

	const inspirationType = useMemo(
		() => (isEdit ? undefined : mapParamsToInspirationType(searchParams)),
		[isEdit, searchParams],
	);

	const form = useGoalForm({
		goalId: goalId ?? undefined,
		initialValues,
		inspirationType,
		onSuccess: async (resultId) => {
			if (isEdit) {
				navigate(-1);
				return;
			}
			if (resultId == null) {
				navigate(getRoute(Routing.Goals.GoalsList), { replace: true });
				return;
			}
			const detailPath = Routing.Goals.GoalDetail.path(String(resultId));
			const detailState = {
				welcomePin: true,
				ownFunds: form.getValues("ownFunds") ?? 0,
			};
			if (!returnToGoalsList) {
				navigate(detailPath, { replace: true, state: detailState });
				return;
			}
			setIsOpeningCreatedGoal(true);
			await queryClient
				.fetchQuery({ ...goalsQueryOptions(ACTIVE_GOAL_STATES), staleTime: 0 })
				.catch(() => undefined);
			await navigate(getRoute(Routing.Goals.GoalsList), { replace: true });
			await navigate(detailPath, { state: detailState });
		},
	});

	const {
		control,
		submit,
		isSubmitting,
		watch,
		trigger,
		minDueDate,
		maxDueDate,
	} = form;

	const [confirmEditOpen, setConfirmEditOpen] = useState(false);
	const requiresEditConfirm = isEdit && goalDto?.fromAgent != null;

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		if (!requiresEditConfirm) {
			await submit(event);
			return;
		}
		event.preventDefault();
		if (await trigger()) {
			setConfirmEditOpen(true);
			return;
		}
		await submit();
	};

	const confirmEdit = async () => {
		setConfirmEditOpen(false);
		await submit();
	};
	const { selectCategory } = useGoalCategoryFieldMemory(form);

	const category = watch("category");
	const dueDate = watch("dueDate");
	const totalValue = watch("totalValue") ?? 0;
	const trimmedNameLength = (watch("name") ?? "").trim().length;
	const nameValid =
		trimmedNameLength >= 1 && trimmedNameLength <= GOAL_NAME_MAX_LENGTH;

	const { savingsTarget, realizationMode, hasMonthlyHero } = deriveGoalAmounts({
		totalValue,
		ownFunds: watch("ownFunds") ?? 0,
		loanAmount: watch("loanAmount") ?? 0,
		dueDate,
	});

	const { sectionTitle } = useSuggestedGoalName(form, category);

	return {
		control,
		submit: handleSubmit,
		confirmEditOpen,
		closeEditConfirm: () => setConfirmEditOpen(false),
		confirmEdit,
		isSubmitting,
		isEdit,
		isOpeningCreatedGoal,
		category,
		selectCategory,
		dueDate,
		savingsTarget,
		realizationMode,
		hasMonthlyHero,
		submitDisabled: totalValue <= 0 || !nameValid || isSubmitting,
		sectionTitle,
		minDueDate,
		maxDueDate,
		goToList,
	};
};
